import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Form } from '@/_shared/components/Form';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { useNextRechargeInDays } from '@/_shared/hooks/useNextRechargeInDays';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentTeamCreditsQuarterCall } from '@/departments/state/api/getDepartmentTeamCreditsQuarterCall';
import { getDepartmentTeamCreditsCall } from '../../state';
import { EmptyState } from './EmptyState';
import { ClusterDataTeam, RecapDataTeam, DivisionByTrendTeam } from './Team';

type Props = {
  departmentId?: string;
  teamId?: string;
  teamName?: string;
};

export const TabCreditsTeam: FC<Props> = ({ departmentId, teamId, teamName }) => {
  const t = useTranslations('departments.tabs.credits.recap');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    execute: executeGetDepartmentTeamCredits,
    loading: loadingGetDepartmentTeamCredits,
    success: successGetDepartmentTeamCredits,
    error: errorGetDepartmentTeamCredits,
    hasNoData,
  } = getDepartmentTeamCreditsCall.hook();

  const {
    execute: executeGetDepartmentTeamCreditsQuarter,
    loading: loadingGetDepartmentTeamCreditsQuarter,
    error: errorGetDepartmentTeamCreditsQuarter,
    defaultQuarter,
    response: responseGetDepartmentTeamCreditsQuarter,
  } = getDepartmentTeamCreditsQuarterCall.hook();

  const { nextMonth, days } = useNextRechargeInDays();

  const { control, watch } = useForm({
    defaultValues: {
      filterQuarter: {
        value: defaultQuarter || '',
      },
    },
  });

  const fieldValues = watch();

  useEffect(() => {
    executeGetDepartmentTeamCreditsQuarter({
      departmentId: departmentId || '',
      teamId: teamId || '',
      successSaga: ({ response }) => {
        executeGetDepartmentTeamCredits({
          departmentId: departmentId || '',
          teamId: teamId || '',
          year: response[0].year,
          quarter: response[0].quarter,
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, teamId]);

  useEffect(() => {
    if (fieldValues.filterQuarter) {
      const [quarter, year] = fieldValues.filterQuarter.value.split('-');
      if (!quarter || !year) {
        return;
      }
      executeGetDepartmentTeamCredits({
        departmentId: departmentId || '',
        teamId: teamId || '',
        year: Number(year),
        quarter: Number(quarter),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, fieldValues.filterQuarter, teamId]);

  if (loadingGetDepartmentTeamCredits || loadingGetDepartmentTeamCreditsQuarter) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader color='primary' size='s' />
      </Box>
    );
  }

  if (
    errorGetDepartmentTeamCreditsQuarter ||
    responseGetDepartmentTeamCreditsQuarter?.length === 0
  ) {
    return <EmptyState />;
  }

  if (errorGetDepartmentTeamCredits || errorGetDepartmentTeamCreditsQuarter) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    successGetDepartmentTeamCredits &&
    !loadingGetDepartmentTeamCredits &&
    !errorGetDepartmentTeamCredits
  ) {
    return (
      <Box>
        {hasNoData ? (
          <EmptyState />
        ) : (
          <Box>
            <Box
              mt='m'
              mb='s'
              display='flex'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
              justifyContent='space-between'
            >
              <Typography
                variant='h1'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                {t('title', { name: teamName })}
              </Typography>
              <Typography variant='p2/light'>
                <RenderLocalesWithComponents
                  text={t('nextRecharge', {
                    date: '!{DATE}!',
                    days: days.toString(),
                  })}
                  placeholders={{
                    '!{DATE}!': (
                      <Typography as='span' variant='p2/light' fontWeight='500'>
                        {nextMonth}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant='p3/light'>{t('descriptionTeam')}</Typography>
            </Box>
            <Box width='300px' mt='s' mb='m'>
              {responseGetDepartmentTeamCreditsQuarter && (
                <Form.FormDropdown
                  // @ts-ignore
                  control={control}
                  name='filterQuarter'
                  items={
                    responseGetDepartmentTeamCreditsQuarter
                      ? responseGetDepartmentTeamCreditsQuarter.map(item => ({
                          label: `Q${item.quarter} - ${item.year}`,
                          value: `${item.quarter}-${item.year}`,
                        }))
                      : []
                  }
                  labelStyle={{ color: 'white', mb: 'xxs' }}
                  data-testid='dropdown-select-campaign'
                  optionTestId='dropdown-select-campaign-option'
                  placeholder={
                    `Q${responseGetDepartmentTeamCreditsQuarter?.[0].quarter} - ${responseGetDepartmentTeamCreditsQuarter?.[0].year}` ||
                    ''
                  }
                />
              )}
            </Box>
            <RecapDataTeam />
            <ClusterDataTeam />
            <DivisionByTrendTeam teamName={teamName || ''} />
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
